import * as easingFunctions from './easingHelpers.js'
/**
 * Universal smooth scroll with dynamic duration based on travel distance
 * @param {Object} options - Configuration options
 * @param {HTMLElement} [options.scrollTarget] - Target element to scroll (defaults to window)
 * @param {number} [options.top] - Target vertical position
 * @param {number} [options.left] - Target horizontal position
 * @param {number} [options.durationFactor=1] - Duration multiplier (ms per 1000px)
 * @param {string} [options.easing='easeInOutQuad'] - Easing function
 * @param {function} [options.onComplete] - Completion callback
 * @param {number} [options.minDuration=250] - Minimum duration in ms
 * @param {number} [options.maxDuration=1500] - Maximum duration in ms
 */
export const smoothScroll = (options = {}) => {
  let {
    scrollTarget,
    top,
    left,
    durationFactor = 1000, // milliseconds per 1000px of travel
    easing = 'easeInOutQuad',
    onComplete = () => {},
    minDuration = 250,
    maxDuration = 1500
  } = options

  // Determine scroll target element (scrollTarget or window)
  scrollTarget = scrollTarget || window
  const isWindow = scrollTarget === window

  // Get current scroll positions
  const startTop = isWindow ? window.pageYOffset : scrollTarget.scrollTop
  const startLeft = isWindow ? window.pageXOffset : scrollTarget.scrollLeft

  // Calculate travel distances
  const travelTop = top !== undefined ? Math.abs(top - startTop) : 0
  const travelLeft = left !== undefined ? Math.abs(left - startLeft) : 0
  const maxTravel = Math.max(travelTop, travelLeft)

  // Calculate dynamic duration
  const rawDuration = (maxTravel / 1000) * durationFactor
  const duration = Math.min(maxDuration, Math.max(minDuration, rawDuration))

  let startTime = null

  const ease = easingFunctions[easing] || easingFunctions.easeInOutQuad

  function animation (currentTime) {
    if (!startTime) startTime = currentTime
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const easedProgress = ease(progress)

    // Apply scroll
    if (top !== undefined) {
      const newTop = startTop + (top - startTop) * easedProgress
      if (isWindow) {
        window.scrollTo(window.pageXOffset, newTop)
      } else {
        scrollTarget.scrollTop = newTop
      }
    }

    if (left !== undefined) {
      const newLeft = startLeft + (left - startLeft) * easedProgress
      if (isWindow) {
        window.scrollTo(newLeft, window.pageYOffset)
      } else {
        scrollTarget.scrollLeft = newLeft
      }
    }

    if (progress < 1) {
      requestAnimationFrame(animation)
    } else {
      onComplete()
    }
  }

  // Start animation
  requestAnimationFrame(animation)
}
